import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Grid,
  CircularProgress,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import Chart from "chart.js/auto";
import MoneyTree from "../../images/svg/money-tree.svg";
import { ReportData, FormData } from "../generate-reports/calculateReport";

// Updated Interface to accept new fields from EmbedGenerateReport
export interface ViewGeneratedReportState {
  formData: FormData;
  reportData: ReportData;
  utilization: number;
  utilityRate: number;
  savedReport?: any;
  // New fields
  customerSplit?: number;
  softwareProviderSplit?: number;
  operatorSplit?: number;
  customerProfit?: number;
  softwareProviderProfit?: number;
  operatorProfit?: number;
  applyTaxBenefits?: boolean;
  taxSavings?: number;
  businessTaxRate?: string;
  section179Limit?: string;
  bonusDepreciationPct?: string;
}

// --- Styled Components ---
const Container = styled(Box)(({ theme }) => ({
  minHeight: "95vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const Content = styled(Box)<{ component?: React.ElementType }>(({ theme }) => ({
  width: "100%",
  maxWidth: "auto",
  padding: theme.spacing(2),
  marginTop: theme.spacing(0),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0),
    maxWidth: "100%",
    marginTop: theme.spacing(0),
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(3),
  position: "relative",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontSize: "35px",
  fontWeight: 700,
  color: "#4b5b5d",
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "20px",
  },
}));

const StyledBackButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "#f2efe9",
  color: "#455354",
  transition: "background 0.24s ease-in-out, color 0.24s ease-in-out",
  "&:hover": {
    background: "linear-gradient(to bottom, #758d8e, #4b5b5d)",
    color: "#f2efe9",
  },
}));

const ProfitContent = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(2),
  background: "linear-gradient(to left, #f4efe6, #4b5b5d)",
  borderRadius: theme.shape.borderRadius,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2.5),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
    flexDirection: "column",
    gap: theme.spacing(1),
  },
}));

const TitleText = styled(Typography)(({ theme }) => ({
  fontSize: "2.8rem",
  fontWeight: 700,
  fontFamily: '"Libre Baskerville", serif',
  color: "#e8e5dc",
  [theme.breakpoints.down("md")]: {
    fontSize: "2.2rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.6rem",
  },
}));

const SubTitleText = styled(Typography)(({ theme }) => ({
  fontSize: "1.2rem",
  fontWeight: 500,
  fontFamily: '"Libre Baskerville", serif',
  color: "#e8e5dc",
  marginTop: theme.spacing(1),
  [theme.breakpoints.down("md")]: {
    fontSize: "1rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.9rem",
  },
}));

const BulletList = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
  "& li": {
    color: "#e8e5dc",
    fontSize: "1rem",
    fontFamily: '"Libre Baskerville", serif',
    fontWeight: 400,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    "&::before": {
      content: '"•"',
      color: "#e8e5dc",
      fontSize: "1.5rem",
      lineHeight: "1rem",
    },
    [theme.breakpoints.down("md")]: {
      fontSize: "0.95rem",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.85rem",
    },
  },
}));

const MoneyTreeImage = styled("img")(({ theme }) => ({
  maxWidth: "250px",
  height: "auto",
  [theme.breakpoints.down("md")]: {
    maxWidth: "180px",
  },
  [theme.breakpoints.down("sm")]: {
    maxWidth: "120px",
  },
}));

const MapContainer = styled(Box)(({ theme }) => ({
  height: "300px",
  width: "100%",
  border: "1px solid #ccc",
  borderRadius: "5px",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    height: "250px",
  },
  [theme.breakpoints.down("sm")]: {
    height: "200px",
  },
}));

const Card = styled(Box)(({ theme }) => ({
  backgroundColor: "#f8f8f8",
  borderRadius: "8px",
  padding: theme.spacing(2),
  width: "100%",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: "1.8rem",
  fontWeight: 600,
  color: "#4b5b5d",
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down("md")]: {
    fontSize: "1.2rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: "#757575",
  fontSize: "1rem",
  fontWeight: 500,
  padding: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.8rem",
    padding: theme.spacing(0.5),
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const StyledTableDataCell = styled(TableCell)(({ theme }) => ({
  color: "#757575",
  fontSize: "0.9rem",
  fontWeight: 500,
  padding: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.7rem",
    padding: theme.spacing(0.5),
  },
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  height: "400px",
  width: "100%",
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: "#ffffff",
  border: "1px solid #eee",
  borderRadius: "8px",
  padding: theme.spacing(2),
  boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
  [theme.breakpoints.down("md")]: {
    height: "350px",
  },
  [theme.breakpoints.down("sm")]: {
    height: "280px",
    padding: theme.spacing(1),
  },
}));

interface EmbedViewGeneratedReportProps {
  state: ViewGeneratedReportState;
  onBack: () => void;
}

const EmbedViewGeneratedReport: React.FC<EmbedViewGeneratedReportProps> = ({
  state,
  onBack,
}) => {
  const { 
    formData, 
    reportData, 
    utilityRate,
    // New Fields
    applyTaxBenefits,
    taxSavings,
    customerSplit,
    softwareProviderSplit,
    operatorSplit,
    customerProfit,
    softwareProviderProfit,
    operatorProfit
  } = state;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const mapRef = useRef<HTMLDivElement>(null);
  const breakEvenChartRef = useRef<HTMLCanvasElement>(null);
  const revenueCostChartRef = useRef<HTMLCanvasElement>(null);
  const co2SavingsChartRef = useRef<HTMLCanvasElement>(null);
  const breakEvenChartInstance = useRef<Chart | null>(null);
  const revenueCostChartInstance = useRef<Chart | null>(null);
  const co2SavingsChartInstance = useRef<Chart | null>(null);
  const [mapLoaded, setMapLoaded] = useState<boolean>(false);

  // Determine if we show profit sharing
  const showProfitSharing = customerSplit && customerSplit < 100;

  // Reusable plugin to add shadow to line charts
  const lineShadowPlugin = useMemo(
    () => ({
      id: "lineShadow",
      beforeDatasetsDraw: (chart: any, _args: any, pluginOptions: any) => {
        const ctx = chart.ctx as CanvasRenderingContext2D;
        ctx.save();
        ctx.shadowColor = pluginOptions?.color || "rgba(0,0,0,0.25)";
        ctx.shadowBlur = pluginOptions?.blur ?? 5;
        ctx.shadowOffsetX = pluginOptions?.offsetX ?? 5;
        ctx.shadowOffsetY = pluginOptions?.offsetY ?? 5;
      },
      beforeDatasetDraw: (chart: any, args: any, pluginOptions: any) => {
        const ctx = chart.ctx as CanvasRenderingContext2D;
        const dataset = chart.data?.datasets?.[args.index] as any;
        const baseAlpha = pluginOptions?.alpha ?? 10;

        const toRgba = (color: any, alpha: number) => {
          if (typeof color !== "string") return `rgba(0,0,0,${alpha})`;
          if (color.startsWith("#")) {
            let r = 0,
              g = 0,
              b = 0;
            if (color.length === 4) {
              r = parseInt(color[1] + color[1], 16);
              g = parseInt(color[2] + color[2], 16);
              b = parseInt(color[3] + color[3], 16);
            } else if (color.length === 7) {
              r = parseInt(color.slice(1, 3), 16);
              g = parseInt(color.slice(3, 5), 16);
              b = parseInt(color.slice(5, 7), 16);
            }
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
          }
          const rgbMatch = color.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/i);
          if (rgbMatch) {
            const [, r, g, b] = rgbMatch;
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
          }
          const rgbaMatch = color.match(
            /^rgba\((\d+),\s*(\d+),\s*(\d+),\s*([0-9]*\.?[0-9]+)\)$/i
          );
          if (rgbaMatch) {
            const [, r, g, b] = rgbaMatch;
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
          }
          return color;
        };

        let borderColor = dataset?.borderColor;
        if (Array.isArray(borderColor) && borderColor.length > 0) {
          borderColor = borderColor[0];
        }
        ctx.shadowColor = toRgba(borderColor, baseAlpha);
      },
      afterDatasetsDraw: (chart: any) => {
        const ctx = chart.ctx as CanvasRenderingContext2D;
        ctx.restore();
      },
    }),
    []
  );

  useEffect(() => {
    const initMap = () => {
      if (!mapRef.current || !window.google) return;

      const defaultPosition = { lat: 40.518715, lng: -74.412095 };
      const address = formData?.address || "981 US Route 1, Edison, NJ, USA";

      const map = new window.google.maps.Map(mapRef.current, {
        center: defaultPosition,
        zoom: 15,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU,
          mapTypeIds: [
            window.google.maps.MapTypeId.ROADMAP,
            window.google.maps.MapTypeId.SATELLITE,
          ],
        },
      });

      // Set map as loaded when tiles are loaded
      window.google.maps.event.addListenerOnce(map, "tilesloaded", () => {
        setMapLoaded(true);
      });

      window.google.maps.event.addListenerOnce(map, "idle", () => {
        setMapLoaded(true);
      });

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { address },
        (
          results: google.maps.GeocoderResult[] | null,
          status: google.maps.GeocoderStatus
        ) => {
          if (
            status === window.google.maps.GeocoderStatus.OK &&
            results &&
            results[0]
          ) {
            const position = results[0].geometry.location;
            map.setCenter(position);

            const marker = new window.google.maps.Marker({
              position,
              map,
              title: "Charging Station",
            });

            const infoWindow = new window.google.maps.InfoWindow({
              content: `<b>Location:</b> ${address}<br><b>Charging Station Site</b>`,
            });
            infoWindow.open(map, marker);
          } else {
             map.setCenter(defaultPosition);
          }
        }
      );
    };

    const loadScript = () => {
      if (window.google && window.google.maps) {
        initMap();
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDZsmG9zzmlEZLY3BpPYeJMfUphIobw-sM&libraries=places`;
      script.async = true;
      script.onload = initMap;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    };

    loadScript();

    // Break Even/Cash Flow Chart
    const breakEvenCtx = breakEvenChartRef.current?.getContext("2d");
    if (breakEvenCtx && reportData) {
      if (breakEvenChartInstance.current)
        breakEvenChartInstance.current.destroy();
      const labels = ["0", "1", "2", "3", "4", "5"];
      
      // Corrected logic for tax benefits in Upfront Cost
      const upfrontCost = applyTaxBenefits 
        ? reportData.upfrontAfterTax || (reportData.install + reportData.hardware + reportData.network)
        : (reportData.install + reportData.hardware + reportData.network);

      const data = (() => {
        const cash = [-upfrontCost];
        for (let i = 0; i < 5; i++) {
          const yVal =
            reportData.chargingRevArr[i] +
            reportData.indirectArr[i] -
            reportData.maintenance / 5 -
            reportData.kWhYearArr[i] * utilityRate;
          cash.push(cash[i] + yVal);
        }
        return cash;
      })();

      breakEvenChartInstance.current = new Chart(breakEvenCtx, {
        type: "line",
        data: {
          labels,
          datasets: [
            {
              label: "Cumulative Cash Flow",
              data,
              borderColor: "#4b5b5d",
              backgroundColor: "#c3d5d6",
              fill: true,
              tension: 0.5,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: false,
              title: { display: true, text: "Cash ($)" },
              grid: {
                color: (ctx) =>
                  ctx.tick.value === 0 ? "#000" : "rgba(0,0,0,0.1)",
                lineWidth: (ctx) => (ctx.tick.value === 0 ? 1 : 1),
              },
            },
            x: {
              title: {
                display: true,
                text: "Years",
              },
            },
          },
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                font: {
                  size: isMobile ? 7 : 10,
                },
              },
            },
          },
        },
      });
    }

    // 5-Year Revenue & Cost Breakdown Chart
    const revenueCostCtx = revenueCostChartRef.current?.getContext("2d");
    if (revenueCostCtx && reportData) {
      if (revenueCostChartInstance.current)
        revenueCostChartInstance.current.destroy();
      const labels = ["Year 1", "Year 2", "Year 3", "Year 4", "Year 5"];
      const datasets = [
        {
          label: "Charging Rev",
          data: reportData.chargingRevArr,
          borderColor: "#4B5B5D",
          backgroundColor: "rgba(75, 91, 93, 0.2)",
          fill: false,
          tension: 1,
        },
        {
          label: "Indirect Rev",
          data: reportData.indirectArr,
          borderColor: "#4682B4",
          backgroundColor: "rgba(70, 130, 180, 0.2)",
          fill: false,
          tension: 1,
        },
        {
          label: "Install Cost",
          data: [reportData.install, 0, 0, 0, 0],
          borderColor: "#B22222",
          backgroundColor: "rgba(178, 34, 34, 0.2)",
          fill: false,
          tension: 1,
        },
        {
          label: "Hardware Cost",
          data: [reportData.hardware, 0, 0, 0, 0],
          borderColor: "#A9A9A9",
          backgroundColor: "rgba(169, 169, 169, 0.2)",
          fill: false,
          tension: 1,
        },
        {
          label: "Network Cost",
          data: [reportData.network, 0, 0, 0, 0],
          borderColor: "#3CB371",
          backgroundColor: "rgba(60, 179, 113, 0.2)",
          fill: false,
          tension: 1,
        },
        {
          label: "Maintenance",
          data: Array(5).fill(reportData.maintenance / 5),
          borderColor: "#FFA500",
          backgroundColor: "rgba(255, 165, 0, 0.2)",
          fill: false,
          tension: 1,
        },
        {
          label: "Utility Cost",
          data: reportData.kWhYearArr.map((k) => k * utilityRate),
          borderColor: "#D3D3D3",
          backgroundColor: "rgba(211, 211, 211, 0.2)",
          fill: false,
          tension: 1,
        },
      ];
      revenueCostChartInstance.current = new Chart(revenueCostCtx, {
        type: "line",
        data: {
          labels,
          datasets: datasets as any,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              type: "linear",
              title: { display: true, text: "USD ($)" },
              min: 0,
            },
            x: {
              title: {
                display: true,
                text: "Years",
              },
            },
          },
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                font: {
                  size: isMobile ? 7 : 10,
                },
              },
            },
            tooltip: {
              callbacks: {
                label: (ctx: any) => {
                  const label = ctx.dataset.label || "";
                  const value = ctx.parsed.y ?? ctx.parsed;
                  return `${label}: $${Number(value).toLocaleString()}`;
                },
              },
            },
          },
        },
        plugins: [lineShadowPlugin],
      });
    }

    // CO2 Savings and Marketing Impact Chart
    const co2SavingsCtx = co2SavingsChartRef.current?.getContext("2d");
    if (co2SavingsCtx && reportData) {
      if (co2SavingsChartInstance.current)
        co2SavingsChartInstance.current.destroy();
      const labels = ["Year 1", "Year 2", "Year 3", "Year 4", "Year 5"];
      const datasets = [
        {
          label: "CO₂ Offset (kg)",
          data: reportData.co2Arr.map((x) => Math.round(x)),
          backgroundColor: "#455354",
          borderColor: "#4b5b5d",
          borderWidth: 1,
          borderRadius: 2,
        },
        {
          label: "Gasoline Replaced (gal)",
          data: reportData.gasArr.map((x) => Math.round(x)),
          backgroundColor: "#758d8e",
          borderColor: "#4b5b5d",
          borderWidth: 1,
          borderRadius: 2,
        },
      ];
      co2SavingsChartInstance.current = new Chart(co2SavingsCtx, {
        type: "bar",
        data: {
          labels,
          datasets: datasets as any,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: "Amount",
                font: {
                  size: isMobile ? 10 : 12,
                },
              },
            },
            x: {
              title: {
                display: true,
                text: "Years",
                font: {
                  size: isMobile ? 10 : 12,
                },
              },
            },
          },
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                font: {
                  size: isMobile ? 7 : 10,
                },
              },
            },
          },
        },
      });
    }

    return () => {
      breakEvenChartInstance.current?.destroy();
      revenueCostChartInstance.current?.destroy();
      co2SavingsChartInstance.current?.destroy();
    };
  }, [reportData, utilityRate, formData, isMobile, lineShadowPlugin, applyTaxBenefits]);

  const reportTableData = reportData
    ? [
        {
          category: "Charger Revenue (5yrs)",
          value: `$${reportData.chargingRev5.toLocaleString()}`,
        },
        {
          category: "Added Dwell Time (per visit)",
          value: reportData.addedDwell,
        },
        {
          category: "Additional Spend (per visit)",
          value: reportData.addSpend,
        },
        { category: "Traffic Increase", value: reportData.traffic },
        {
          category: "Indirect Revenue (5yrs)",
          value: `$${reportData.indirectRev5.toLocaleString()}`,
        },
        {
          category: "Total Revenue",
          value: `$${reportData.totalRev5.toLocaleString()}`,
        },
        {
          category: "-Install Cost",
          value: `$${reportData.install.toLocaleString()}`,
        },
        {
          category: "-Hardware Cost",
          value: `$${reportData.hardware.toLocaleString()}`,
        },
        {
          category: "-Network Cost",
          value: `$${reportData.network.toLocaleString()}`,
        },
        {
          category: "-Maintenance",
          value: `$${reportData.maintenance.toLocaleString()}`,
        },
        {
          category: "-Utility Cost",
          value: `$${reportData.utilityCost5.toLocaleString()}`,
        },
        {
          category: "-Grants/Subsidies",
          value: `$${reportData.grant.toLocaleString()}`,
        },
        // --- ADDED: Tax Savings Row if applicable ---
        ...(applyTaxBenefits && taxSavings && taxSavings > 0
          ? [
              {
                category: "Section 179 / 168(k) Tax Savings",
                value: `$${Math.round(taxSavings).toLocaleString()}`,
                isBenefit: true,
              },
            ]
          : []),
      ]
    : [];

  return (
    <Container>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Content>
          <HeaderContainer>
            <WelcomeText>L2 Charging Station Report</WelcomeText>
            <Tooltip title="Back to Generate Report">
              <StyledBackButton aria-label="back" onClick={onBack}>
                <ArrowBack />
              </StyledBackButton>
            </Tooltip>
          </HeaderContainer>
          {reportData ? (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <ProfitContent>
                  <Box>
                    <SubTitleText>5-Year Summary</SubTitleText>
                    {/* Display Customer Share if profit sharing, else Net Profit */}
                    <TitleText>
                      ${showProfitSharing 
                          ? customerProfit?.toLocaleString() 
                          : reportData.netProfit5.toLocaleString()
                       }
                       {showProfitSharing && <span style={{fontSize: "1rem"}}> (Customer Share)</span>}
                    </TitleText>
                    <BulletList>
                      <li>24 Month Break Even Point</li>
                      <li>50,000 kg CO₂ Offset</li>
                      <li>21,000 gallons of Gasoline Saved</li>
                    </BulletList>
                  </Box>
                  {!isMobile && (
                    <MoneyTreeImage src={MoneyTree} alt="Money Tree" />
                  )}
                </ProfitContent>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Card>
                  <Title>Financial Breakdown</Title>
                  <Box sx={{ overflowX: "auto" }}>
                    <Table>
                      <TableHead>
                        <TableRow
                          style={{
                            background:
                              "linear-gradient(to left, #f4efe6, #bcc4c4)",
                          }}
                        >
                          <StyledTableCell>Category</StyledTableCell>
                          <StyledTableDataCell>
                            Amount/Value
                          </StyledTableDataCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportTableData.map((item: any, index) => (
                          <StyledTableRow key={index}>
                            <StyledTableDataCell sx={item.isBenefit ? { color: '#2e7d32', fontWeight: 700 } : {}}>
                              {item.category}
                            </StyledTableDataCell>
                            <StyledTableDataCell sx={item.isBenefit ? { color: '#2e7d32', fontWeight: 700 } : {}}>
                              {item.value}
                            </StyledTableDataCell>
                          </StyledTableRow>
                        ))}
                        
                        {/* PROFIT SHARING LOGIC IN TABLE */}
                        {showProfitSharing ? (
                          <>
                            <TableRow style={{ backgroundColor: '#f0f4f4' }}>
                                <StyledTableCell><strong>Net Profit (Total)</strong></StyledTableCell>
                                <StyledTableDataCell><strong>${reportData.netProfit5.toLocaleString()}</strong></StyledTableDataCell>
                            </TableRow>
                            <TableRow>
                                <StyledTableCell>Customer Share ({customerSplit}%)</StyledTableCell>
                                <StyledTableDataCell>${customerProfit?.toLocaleString()}</StyledTableDataCell>
                            </TableRow>
                            {softwareProviderSplit && softwareProviderSplit > 0 && (
                                <TableRow>
                                    <StyledTableCell>Software Provider Share ({softwareProviderSplit}%)</StyledTableCell>
                                    <StyledTableDataCell>${softwareProviderProfit?.toLocaleString()}</StyledTableDataCell>
                                </TableRow>
                            )}
                            {operatorSplit && operatorSplit > 0 && (
                                <TableRow>
                                    <StyledTableCell>Operator Share ({operatorSplit}%)</StyledTableCell>
                                    <StyledTableDataCell>${operatorProfit?.toLocaleString()}</StyledTableDataCell>
                                </TableRow>
                            )}
                          </>
                        ) : (
                           // Standard View
                           <TableRow
                            style={{
                              background:
                                "linear-gradient(to left, #f4efe6, #bcc4c4)",
                            }}
                          >
                            <StyledTableCell>Net Profit</StyledTableCell>
                            <StyledTableDataCell>
                              ${reportData.netProfit5.toLocaleString()}
                            </StyledTableDataCell>
                          </TableRow>
                        )}
                        
                      </TableBody>
                    </Table>
                  </Box>
                </Card>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <MapContainer ref={mapRef} />
                {!mapLoaded && (
                  <Box sx={{ textAlign: "center", mt: 1, color: "#666" }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading map...
                  </Box>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Card>
                  <Title>Break Even/Cash Flow</Title>
                  <ChartContainer>
                    <canvas ref={breakEvenChartRef} />
                  </ChartContainer>
                </Card>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Card>
                  <Title>5-Year Revenue & Cost</Title>
                  <ChartContainer>
                    <canvas ref={revenueCostChartRef} />
                  </ChartContainer>
                </Card>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Card>
                  <Title>CO2 Savings & Impact</Title>
                  <ChartContainer>
                    <canvas ref={co2SavingsChartRef} />
                  </ChartContainer>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Typography color="error" variant="h6" align="center">
              No report data available. Please generate a report.
            </Typography>
          )}
        </Content>
      </motion.div>
    </Container>
  );
};

export default EmbedViewGeneratedReport;